import { caller } from '@shared/utils/caller'
import { ApiResponse } from '@shared/utils/api-response'
import { formDataFlat } from '@shared/utils/form-data-flat'
import { localURLMaker, urlMaker } from '@shared/utils/url-maker'
import { appendToFormData } from '@shared/utils/apply-to-form-data'
import { NextTableMetaResponse } from '@shared/modules/next-table/types'
import { nextTableMetaMapper } from '@shared/modules/next-table/utils/meta-mapper'

import { Insurance, InsuranceFormData, InsuranceFormDataApi, InsuranceFormErrors } from '../types'
import { InsuranceMappers } from '../mappers'

export class InsurancesService {
  public static async getOneById(id: string | number): Promise<ApiResponse<200, Insurance> | ApiResponse<400 | 500, string>> {
    const url = urlMaker('/api/insurances/info', { insurance_id: id })

    try {
      const response = await caller(url)

      if (response.ok) {
        const { data } = await response.json()
        return new ApiResponse(200, InsuranceMappers.fromApi(data), null)
      } else {
        return new ApiResponse(400, 'Məlumatlar əldə edilə bilmədi.', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null)
    }
  }

  public static async getAll(
    query: Record<string, any>,
  ): Promise<ApiResponse<200, Insurance[], NextTableMetaResponse> | ApiResponse<400 | 500, string>> {
    const url = urlMaker('/api/insurances', query)

    try {
      const response = await caller(url)

      if (response.ok) {
        const { data, ...meta } = await response.json()
        return new ApiResponse(200, InsuranceMappers.collectionFromApi(data), nextTableMetaMapper(meta))
      } else {
        return new ApiResponse(400, 'Məlumatlar əldə edilə bilmədi', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Şəbəkə ilə əlaqə qurula bilmədi', null)
    }
  }

  public static async createOrUpdate(
    values: InsuranceFormData,
    type: string = 'create',
  ): Promise<ApiResponse<200> | ApiResponse<422, InsuranceFormErrors> | ApiResponse<400 | 500, string>> {
    const url = type === 'create' ? urlMaker('/api/insurances/create') : urlMaker('/api/insurances/edit')
    const body = new FormData()

    appendToFormData<InsuranceFormDataApi>(formDataFlat(InsuranceMappers.formDataToApi(values)), body)

    try {
      const response = await caller(url, { body, method: 'POST' })

      if (response.ok) {
        return new ApiResponse(200, null, null)
      } else if (response.status === 422) {
        const { errors } = await response.json()

        return new ApiResponse(422, InsuranceMappers.formErrorsFromApi(errors), null)
      } else {
        return new ApiResponse(400, 'Əməliyyat aparıla bilmədi.', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null)
    }
  }

  public static async actionByIds(ids: (string | number)[], action: string): Promise<ApiResponse<200> | ApiResponse<400 | 500, string>> {
    const url = urlMaker(localURLMaker('/api/insurances/:action', { action }))
    const body = new FormData()

    appendToFormData(formDataFlat({ insurance_id: ids }), body)

    try {
      const response = await caller(url, { method: 'POST', body })

      if (response.ok) {
        return new ApiResponse(200, null, null)
      } else {
        return new ApiResponse(400, 'Əməliyyat aparıla bilmədi.', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Şəbəkə ilə əlaqə qurula bilmədi.', null)
    }
  }

  public static async deleteById(ids: (string | number)[]): Promise<ApiResponse<200, string> | ApiResponse<500, string>> {
    try {
      const url = urlMaker('/api/insurances/remove')
      const body = new FormData()

      appendToFormData(formDataFlat({ insurance_id: ids }), body)
      const response = await caller(url, { method: 'POST', body })
      if (!response.ok) {
        return new ApiResponse(500, 'Əməliyyat aparıla bilmədi.', null)
      } else {
        return new ApiResponse(200, 'Şirkət uğurla silindi.', null)
      }
    } catch (e) {
      return new ApiResponse(500, 'Əməliyyat aparıla bilmədi.', null)
    }
  }
}
